import { v4 as uuidv4, v7 as uuidv7 } from 'uuid';

/**
 * INV-7 METADATA IDENTITY.
 *
 * Every metadata row carries three identity columns:
 *  - `id`            immutable UUID, the primary key, local to this instance
 *  - `universal_id`  stable across instances, used for diffing and seeding
 *  - `origin`        who owns the row (system-seeded vs. user-created)
 *
 * `name` is a mutable attribute and is never used as an identity.
 */

/**
 * Where a metadata row came from, per `docs/decisions/metadata-model.md`:
 * created on this instance, seeded from a template, or brought in by an import.
 * It drives whether a template sync may rewrite the row.
 *
 * This is NOT the system-vs-user axis - that is the `is_system` flag on the
 * metadata row, which decides whether users may rename or drop it.
 */
export const METADATA_ORIGINS = ['local', 'template', 'import'] as const;
export type MetadataOrigin = (typeof METADATA_ORIGINS)[number];

/**
 * Primary key for a metadata row. UUIDv7 is time-ordered, which keeps B-tree
 * inserts local instead of scattering them like UUIDv4.
 */
export function newId(): string {
  return uuidv7();
}

/**
 * A universal id is a namespaced, lowercase, colon-separated path.
 * Examples: `core:object:person`, `local:018f4b1c-8f2a-7000-8000-4f1d2e3a5b6c`.
 */
export const UNIVERSAL_ID_PATTERN = /^[a-z][a-z0-9]*(?::[a-z0-9][a-z0-9_-]*)+$/;

export function isUniversalId(value: string): boolean {
  return UNIVERSAL_ID_PATTERN.test(value);
}

/**
 * Universal id for USER-created metadata. Random, but namespaced so it is
 * immediately obvious that it did not come from a seed and must never be
 * expected on another instance.
 */
export function newUniversalId(): string {
  return `local:${uuidv7()}`;
}

/**
 * Universal id for SYSTEM-owned metadata. Deterministic and human-readable so
 * the same seed produces the same id on every instance, which is what makes
 * cross-instance diffing possible.
 */
export function systemUniversalId(namespace: string, ...path: readonly string[]): string {
  const id = [namespace, ...path].join(':');
  if (!isUniversalId(id)) {
    throw new Error(`Invalid universal id ${JSON.stringify(id)}: expected lowercase a:b[:c]`);
  }
  return id;
}

/**
 * Identity of a SELECT / MULTI_SELECT option.
 *
 * The option id IS the physical PostgreSQL enum label, so it is immutable and
 * carries no meaning: renaming an option changes its `label` in metadata and
 * touches no DDL at all. Deriving the enum label from the human text instead is
 * what made a rename a schema change (and needed a same-position heuristic to
 * tell a rename from a remove+add). See docs/decisions/field-types.md.
 *
 * 12 hex characters taken from the RANDOM part of a UUID, never the leading part:
 * our ids are UUIDv7 whose leading hex is a millisecond timestamp, so a prefix is
 * identical for everything minted in the same window (the same trap documented in
 * server/src/metadata/names.ts).
 */
export const SELECT_OPTION_ID_PATTERN = /^opt_[0-9a-f]{12}$/;

export function isSelectOptionId(value: string): boolean {
  return SELECT_OPTION_ID_PATTERN.test(value);
}

export function newSelectOptionId(): string {
  return `opt_${uuidv4().replace(/-/g, '').slice(-12)}`;
}
